import React, { useState } from 'react';
import { Search, Package, ChevronRight, ImagePlus, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useStore } from 'context/StoreContext';
import { useApi } from 'hooks/useApi';
import { useDebounce } from 'hooks/useDebounce';
import { ProductService } from 'services/catalog/product.service';
import { StoreProduct } from 'dtos/catalog/product.dto';
import { Button } from 'components/common/Button';
import { EmptyState, LoadingScreen } from 'components/common/Spinner';
import { formatCurrency } from 'utils/formatters';
import { CostDocumentService } from 'services/costs/cost-document.service';

const PAGE_SIZE = 20;

export default function CostManagementPage() {
  const { org, currentStore } = useStore();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  const { data: products, loading } = useApi(
    () => ProductService.list(org?.id ?? '', currentStore?.id ?? '', debouncedSearch || undefined, undefined, 1, PAGE_SIZE),
    [org?.id, currentStore?.id, debouncedSearch]
  );
  const { data: pendingDocuments } = useApi(
    () => CostDocumentService.listPending(org?.id ?? '', currentStore?.id ?? ''),
    [org?.id, currentStore?.id]
  );
  const costSummary = products?.meta.cost_summary;

  if (loading && !products) return <LoadingScreen />;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Package size={20} className="text-gray-600" />
            <h1 className="text-xl font-bold text-gray-900">Product Costs</h1>
          </div>
          <p className="mt-1 text-sm text-gray-500">Manage acquisition costs used for profitability calculations.</p>
        </div>
        <div className="flex flex-wrap gap-2">
        <Button onClick={() => navigate('/costs/capture')}>
          <ImagePlus size={15} /> Capture Receipt
        </Button>
        </div>
      </div>

      {!!pendingDocuments?.length && <button type="button" onClick={() => navigate('/costs/pending')} className="flex w-full items-center gap-2 rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-left text-sm text-orange-800"><AlertTriangle size={16} /> {pendingDocuments.length} receipt{pendingDocuments.length === 1 ? '' : 's'} need attention</button>}

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-md border border-gray-200 bg-white p-4"><p className="text-xs text-gray-500">Products</p><p className="mt-1 text-xl font-semibold text-gray-900">{costSummary?.total_products ?? products?.meta.total ?? 0}</p></div>
        <div className="rounded-md border border-green-200 bg-green-50 p-4"><p className="text-xs text-green-700">With current cost</p><p className="mt-1 text-xl font-semibold text-green-800">{costSummary?.products_with_cost ?? 0}</p></div>
        <div className="rounded-md border border-orange-200 bg-orange-50 p-4"><p className="text-xs text-orange-700">Cost unavailable</p><p className="mt-1 text-xl font-semibold text-orange-800">{costSummary?.products_without_cost ?? 0}</p></div>
      </div>

      <div className="relative max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Search products..."
          className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {!products?.data.length ? (
          <EmptyState icon={<Package size={40} />} title="No products found" message="Try a different product search." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Cost</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {products.data.map((product: StoreProduct) => {
                  const hasCost = product.current_cost !== null && product.current_cost !== undefined;
                  return (
                    <tr
                      key={product.id}
                      className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-gray-50"
                      onClick={() => navigate(`/products/${product.product_id}`)}
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{product.product?.product_name}</p>
                        <p className="text-xs text-gray-400">{product.product?.upc ?? product.product?.sku ?? 'No identifier'}</p>
                      </td>
                      <td className={`px-4 py-3 font-mono font-semibold ${hasCost ? 'text-gray-900' : 'text-orange-600'}`}>
                        {hasCost ? formatCurrency(product.current_cost, 2) : 'Not available'}
                      </td>
                      <td className="px-4 py-3 text-gray-500">{hasCost ? (product.cost_source ?? 'Current cost') : 'Not available'}</td>
                      <td className="px-4 py-3 text-right text-gray-400"><ChevronRight size={16} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Button variant="ghost" onClick={() => navigate('/costs/pending')}>View Pending Reviews</Button>
    </div>
  );
}
